import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const EVENT_GROUPS = [
  {
    label: "Bookings",
    events: [
      "booking.created", "booking.paid", "booking.confirmed", "booking.completed",
      "booking.canceled", "booking.rescheduled",
    ],
  },
  {
    label: "Digital orders",
    events: [
      "order.created", "order.paid", "order.requirements_submitted",
      "order.delivered", "order.revision_requested", "order.completed",
    ],
  },
  {
    label: "Requests & proposals",
    events: ["request.published", "request.proposal_received", "proposal.accepted", "proposal.declined"],
  },
  {
    label: "Messaging",
    events: ["message.new"],
  },
  {
    label: "Payments",
    events: ["payment.captured", "payment.refunded", "payout.scheduled", "payout.paid"],
  },
  {
    label: "Reviews & disputes",
    events: ["review.received", "review.response_posted", "review.request", "dispute.opened", "dispute.responded", "dispute.resolved"],
  },
];

const CHANNELS = ["in_app", "email", "sms"];
const DEFAULT_ON = { in_app: true, email: true, sms: false };

export default function NotificationPreferencesPage() {
  const [prefs, setPrefs] = useState({});
  const [saving, setSaving] = useState(false);
  useDocumentTitle("Notification preferences");

  useEffect(() => {
    api.get("/notifications/preferences").then((r) => setPrefs(r.data?.prefs || {})).catch(() => {});
  }, []);

  const get = (event, ch) => {
    const row = prefs[event];
    if (row && ch in row) return !!row[ch];
    return DEFAULT_ON[ch];
  };

  const toggle = (event, ch) => {
    setPrefs((prev) => ({ ...prev, [event]: { ...(prev[event] || {}), [ch]: !get(event, ch) } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/notifications/preferences", { prefs });
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardShell>
      <div className="max-w-4xl">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Notifications · Preferences</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">What should we ping you about?</h1>
            <p className="text-[#252A34]/70 text-sm mt-1">In-app + email are on by default. Text messages are simulated in dev.</p>
          </div>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-medium disabled:opacity-50" data-testid="notif-prefs-save">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>

        <div className="space-y-6">
          {EVENT_GROUPS.map((g) => (
            <div key={g.label} className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`prefs-group-${g.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="px-5 py-3 border-b border-slate-100 font-display font-semibold text-[#071b22]">{g.label}</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#252A34]/60 bg-slate-50">
                    <th className="px-5 py-2">Event</th>
                    {CHANNELS.map((c) => <th key={c} className="px-3 py-2 text-center">{c === "in_app" ? "In-app" : c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {g.events.map((ev) => (
                    <tr key={ev} className="border-t border-slate-100">
                      <td className="px-5 py-2.5 text-[#252A34]">{ev.replace(/\./g, " · ").replace(/_/g, " ")}</td>
                      {CHANNELS.map((c) => (
                        <td key={c} className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={get(ev, c)}
                            onChange={() => toggle(ev, c)}
                            data-testid={`prefs-toggle-${ev}-${c}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </DashboardShell>
  );
}
